import { IChatRoomMessage } from '@commenda/types/src/chat-room/types';
import { useCallback, useEffect, useState } from 'react';

const useChatMessagesNavigationControls = ({
  searchQuery,
  filteredMessages,
  messages,
  selectedMessageId,
  clearSelectedMessageId,
}: {
  searchQuery: string;
  filteredMessages: IChatRoomMessage[];
  messages: IChatRoomMessage[];
  selectedMessageId: string | null;
  clearSelectedMessageId: () => void;
}) => {
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [searchMatches, setSearchMatches] = useState<
    { messageId: string; matchIndex: number }[]
  >([]);

  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      setSearchMatches([]);
      setCurrentSearchIndex(0);
      return;
    }

    const term = searchQuery.trim().toLowerCase();
    // Ignore too-short queries to avoid false positives ("matches everything")
    if (term.length < 2) {
      setSearchMatches([]);
      setCurrentSearchIndex(0);
      return;
    }

    const matches: { messageId: string; matchIndex: number }[] = [];
    let matchIndex = 0;

    // Collect matches starting from the newest message so index 0 is most recent
    for (let i = (filteredMessages?.length || 0) - 1; i >= 0; i -= 1) {
      const message = filteredMessages[i];
      let hasMatch = false;

      // Search in message content
      const messageContent = message.message?.toLowerCase() || '';
      if (messageContent.includes(term)) {
        hasMatch = true;
      }

      // Search in reply message content if exists
      if (!hasMatch) {
        const replyMessage = message.replyToMessageId
          ? messages.find((m) => m.id === message.replyToMessageId)
          : null;
        const replyContent = replyMessage?.message?.toLowerCase() || '';
        if (replyContent.includes(term)) {
          hasMatch = true;
        }
      }

      // Only add the message once if it has any match
      if (hasMatch) {
        // eslint-disable-next-line no-plusplus
        matches.push({ messageId: message.id, matchIndex: matchIndex++ });
      }
    }

    setSearchMatches(matches);
    setCurrentSearchIndex(0);
  }, [searchQuery, filteredMessages, messages]);

  const goToNextSearchResult = useCallback(() => {
    setCurrentSearchIndex((prev) => {
      const newIndex = prev + 1;
      const clampedIndex = Math.max(
        0,
        Math.min(newIndex, searchMatches.length - 1)
      );
      return clampedIndex;
    });
  }, [searchMatches.length]);

  const goToPreviousSearchResult = useCallback(() => {
    setCurrentSearchIndex((prev) => {
      const newIndex = prev - 1;
      const clampedIndex = Math.max(
        0,
        Math.min(newIndex, searchMatches.length - 1)
      );
      return clampedIndex;
    });
  }, [searchMatches.length]);

  useEffect(() => {
    if (!searchQuery || !searchQuery.trim() || searchMatches.length === 0)
      return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextSearchResult();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousSearchResult();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    searchQuery,
    searchMatches.length,
    goToNextSearchResult,
    goToPreviousSearchResult,
  ]);

  useEffect(() => {
    if (selectedMessageId && clearSelectedMessageId) {
      const timer = setTimeout(() => {
        clearSelectedMessageId();
      }, 500);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [selectedMessageId, clearSelectedMessageId]);

  return {
    currentSearchIndex,
    searchMatches,
    setCurrentSearchIndex,
    setSearchMatches,
    goToNextSearchResult,
    goToPreviousSearchResult,
  };
};

export { useChatMessagesNavigationControls };
